import { api, apiDownload } from './client'
import type { JobDetail, JobLine, JobLineStatus, JobSummary, Paged } from './types'

export interface JobListQuery {
  page?: number
  pageSize?: number
  status?: string
  type?: string
}

export const jobsApi = {
  list(query: JobListQuery = {}): Promise<Paged<JobSummary>> {
    return api.get<Paged<JobSummary>>('/jobs', {
      query: {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 50,
        status: query.status,
        type: query.type,
      },
    })
  },

  get(id: string, signal?: AbortSignal): Promise<JobDetail> {
    return api.get<JobDetail>(`/jobs/${id}`, { signal })
  },

  lines(id: string, status?: JobLineStatus, page = 1, pageSize = 50): Promise<Paged<JobLine>> {
    return api.get<Paged<JobLine>>(`/jobs/${id}/lines`, { query: { status, page, pageSize } })
  },

  cancel(id: string): Promise<void> {
    return api.post<void>(`/jobs/${id}/cancel`)
  },

  /** Re-queues only the failed lines and returns the new job id. */
  retry(id: string): Promise<{ jobId: string }> {
    return api.post<{ jobId: string }>(`/jobs/${id}/retry`)
  },

  updateLine(id: string, lineId: string, values: Record<string, string | null>): Promise<JobLine> {
    return api.patch<JobLine>(`/jobs/${id}/lines/${lineId}`, { values })
  },

  /** Downloads the job's errored lines CSV, authenticated (see apiDownload). */
  async downloadErrors(id: string): Promise<void> {
    await apiDownload(`/jobs/${id}/errors.csv`, `job-${id.slice(0, 8)}_errors.csv`)
  },

  /**
   * Downloads an Export job's result workbook, authenticated (see apiDownload).
   * The file name comes from the server, which names it after the entity and
   * the day the job ran.
   */
  async downloadResult(id: string): Promise<void> {
    await apiDownload(`/jobs/${id}/export.xlsx`, null)
  },
}
